/**
 * CA CDL Class A trainer — versioned curriculum (TR-1). Curriculum is CODE, not
 * DB: the JSON here is a verbatim extract of the design's content (regenerate via
 * `node scripts/extract-training-content.mjs`), never hand-edited or paraphrased.
 *
 * CONTENT_VERSION is pinned onto every exam attempt (fix J) so a later content
 * bump leaves old attempts graded on the version they were taken under. Bump it
 * whenever the extracted JSON changes.
 */
import testsJson from './tests.json';
import lessonsJson from './lessons.json';
import questionsJson from './questions.json';
import flashcardsJson from './flashcards.json';
import navSectionsJson from './navSections.json';
import configJson from './config.json';

/** Bump on any content change. */
export const CONTENT_VERSION = 'ca-cdl-2026-09-14';

export type TestKey = 'gk' | 'ab' | 'cv';

export interface TrainingTest {
  name: string;
  /** Questions on the DMV exam (drawn from the larger bank). */
  qCount: number;
  /** Passing score (number correct). */
  pass: number;
  mins: string;
  desc: string;
}

export interface TrainingLesson {
  id: string;
  test: TestKey;
  num: number;
  title: string;
  blurb: string;
  topics: string[];
  /** Rich HTML body (rendered sanitized in the UI). */
  body: string;
}

export interface TrainingQuestion {
  id: string;
  test: TestKey;
  topic: string;
  q: string;
  choices: string[];
  /** Index into `choices` of the correct answer. */
  answer: number;
  exp: string;
}

export interface TrainingFlashcard {
  id: string;
  deck: string;
  front: string;
  back: string;
}

/** [sectionLabel, navKeys] — drives the sidebar/tab grouping. */
export type NavSection = [string, string[]];

export const tests = testsJson as Record<TestKey, TrainingTest>;
export const lessons = lessonsJson as TrainingLesson[];
export const questions = questionsJson as TrainingQuestion[];
export const flashcards = flashcardsJson as TrainingFlashcard[];
export const navSections = navSectionsJson as NavSection[];
export const config = configJson as Record<string, string>;

export const TEST_KEYS: TestKey[] = Object.keys(tests) as TestKey[];

/** The full question BANK for a test (larger than the exam's qCount — the exam samples it). */
export function questionsForTest(test: TestKey): TrainingQuestion[] {
  return questions.filter((q) => q.test === test);
}

export function lessonsForTest(test: TestKey): TrainingLesson[] {
  return lessons.filter((l) => l.test === test).sort((a, b) => a.num - b.num);
}

/**
 * A deterministic fingerprint of the GRADED content — every question's answer key
 * plus each test's pass mark and count. The exam grader is authoritative on this,
 * so any change to it MUST ship with a CONTENT_VERSION bump (binding fix D): the
 * manifest test pins `MANIFEST_HASH` and fails if the graded content drifts from it.
 */
export function manifestHash(): string {
  const parts: string[] = [];
  for (const key of [...TEST_KEYS].sort()) {
    parts.push(`${key}:${tests[key].qCount}:${tests[key].pass}`);
  }
  for (const q of [...questions].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))) {
    parts.push(`${q.id}=${q.answer}`);
  }
  const s = parts.join('|');
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(16);
}

/**
 * Pinned graded-content fingerprint for {@link CONTENT_VERSION}. If the manifest
 * test fails, the answer keys or pass marks changed: bump CONTENT_VERSION and set
 * this to the new manifestHash() — never just update the hash.
 */
export const MANIFEST_HASH = '4e528fc8';
